import { Point as GeoJSONPoint, Polygon as GeoJSONPolygon } from "geojson";

import { PROJECTIONS } from "../constants";
import { transformGeoJSON } from "../esri-map/plugins/helper";

export const fetchGeoJSON = async (url) => {
  const response = await fetch(url);
  const data = await response.json();
  return data;
};

export const isPolygon = async (url: string): Promise<boolean> => {
  try {
    // Fetch GeoJSON data
    const response = await fetch(url);
    const data = await response.json();

    // Check if data is valid and contains features
    if (data?.features && data.features.length > 0) {
      // Extract geometry types from features
      const types = data.features.map((feature) => feature.geometry.type);

      // Check if it contains Polygon or MultiPolygon
      const hasPolygon = types.some(
        (type) => type === "Polygon" || type === "MultiPolygon"
      );

      return hasPolygon;
    } else {
      console.error("Invalid GeoJSON data or no features found.");
    }
  } catch (error) {
    console.error("Error fetching or parsing GeoJSON data:", error);
  }

  // File does not contain any polygon
  return false;
};

const computeRestrictedLayers = async (layers) => {
  const updatedRestrictedLayersAndFeatures = [];
  if (layers.length) {
    for (let i = 0; i < layers.length; i++) {
      const { category, url, metadata, hidden } = layers[i];
      if (category === "GeoJSON" && metadata.restrictPins && !hidden) {
        let geojson = await fetchGeoJSON(url);
        const crsName = geojson?.crs?.properties?.name;
        const crsVal = crsName?.split(":")?.pop() || "4326";
        const isCrs =
          !crsName?.includes("CRS84") || !crsName?.includes("4326") || !crsName;

        geojson = transformGeoJSON(
          geojson,
          isCrs,
          PROJECTIONS[`${crsVal}`],
          PROJECTIONS["4326"]
        );

        const isLayerPolygon = await isPolygon(url);
        if (isLayerPolygon) {
          updatedRestrictedLayersAndFeatures.push(...geojson.features);
        }
      }
    }
  }
  return updatedRestrictedLayersAndFeatures;
};

export const isAnyMarkerInsidePolygon = async (
  defaultLng: number,
  defaultLat: number,
  layers: any[],
  gj: { pointInPolygon(point: GeoJSONPoint, polygon: GeoJSONPolygon): boolean }
): Promise<boolean> => {
  const restrictedLayers = await computeRestrictedLayers(layers);

  if (restrictedLayers.length) {
    return restrictedLayers.some((layer) => {
      const geometryType = layer.geometry?.type;
      const geometryCoords = layer.geometry?.coordinates;

      if (!geometryCoords || !geometryType) {
        return false; // Skip invalid geometries
      }

      switch (geometryType) {
        case "Polygon":
          // Check if the point is inside the single Polygon
          return geometryCoords.some((coordinates) => {
            return gj.pointInPolygon(
              { type: "Point", coordinates: [defaultLng, defaultLat] },
              { type: "Polygon", coordinates: [coordinates] }
            );
          });

        case "MultiPolygon":
          // Check if the point is inside any Polygon in the MultiPolygon
          return geometryCoords.some((polygon) => {
            return polygon.some((coordinates) => {
              return gj.pointInPolygon(
                { type: "Point", coordinates: [defaultLng, defaultLat] },
                { type: "Polygon", coordinates: [coordinates] }
              );
            });
          });

        default:
          console.warn(`Unsupported geometry type: ${geometryType}`);
          return false;
      }
    });
  }

  return true; // Return true if there are no restricted layers
};
